import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mic, Volume2, Headphones, Radio } from 'lucide-react';
import { deviceManager } from '../../services/deviceManager'; // Import DeviceManager
import { volumeManager } from '../../services/volumeManager'; // Import VolumeManager
import { audioVolumeManager } from '../../services/audioVolumeManager'; // Import AudioVolumeManager
import VoiceSelector from './VoiceSelector';

const SettingsSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionIcon = styled.div`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.primary[500]};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: ${({ theme }) => theme.spacing.sm} 0 0 0;
  line-height: 1.5;
`;

const WakeWordSection = styled.div`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 48px;
  height: 24px;
  background: ${({ $enabled, theme }) => 
    $enabled ? theme.colors.primary[500] : theme.colors.gray[600]
  };
  border-radius: 12px;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $enabled }) => $enabled ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: all ${({ theme }) => theme.transitions.fast};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
  gap: ${({ theme }) => theme.spacing.md};
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.sm};
    
    &:first-child {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingLabel = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const SettingDescription = styled.p`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const DeviceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const DeviceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const DeviceTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const CurrentDeviceStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary[500]}08, ${({ theme }) => theme.colors.primary[600]}05);
  border: 1px solid ${({ theme }) => theme.colors.primary[500]}20;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.primary[300]};
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  transition: all ${({ theme }) => theme.transitions.smooth};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: ${({ theme }) => theme.spacing.sm};
    
    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 200px;
    }
  }
`;

const CompactStatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status?.online || '#10b981'};
  flex-shrink: 0;
  animation: pulse 2s infinite;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.status?.online || '#10b981'}20;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.status?.online || '#10b981'}20, 0 0 0 0 ${({ theme }) => theme.colors.status?.online || '#10b981'}20;
    }
    50% {
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.status?.online || '#10b981'}20, 0 0 0 6px ${({ theme }) => theme.colors.status?.online || '#10b981'}10;
    }
    100% {
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.status?.online || '#10b981'}20, 0 0 0 0 transparent;
    }
  }
`;

const DeviceTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  letter-spacing: 0.025em;
`;

const DeviceIcon = styled.div`
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.primary[400]};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const CurrentDeviceCard = styled.div`
  background: ${({ theme }) => theme.colors.primary[500]}08;
  border: 1px solid ${({ theme }) => theme.colors.primary[500]}30;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.online};
  flex-shrink: 0;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 ${({ theme }) => theme.colors.status.online}40;
    }
    70% {
      box-shadow: 0 0 0 6px transparent;
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
    }
  }
`;

const CurrentDeviceLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary[300]};
  font-weight: 500;
  flex: 1;
`;

const SelectLabel = styled.label`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  display: block;
  font-weight: 500;
  letter-spacing: 0.025em;
`;

const DeviceSelect = styled.select`
  width: 100%;
  background: linear-gradient(145deg, ${({ theme }) => theme.colors.dark.elevated}, ${({ theme }) => theme.colors.dark.surface});
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.text};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.smooth};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary[500]}15, 0 4px 12px rgba(0, 0, 0, 0.12);
  }
  
  option {
    background: ${({ theme }) => theme.colors.dark.elevated};
    color: ${({ theme }) => theme.colors.dark.text};
    padding: ${({ theme }) => theme.spacing.sm};
    font-weight: 500;
  }
`;

const ActiveDeviceIndicator = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primary[500]}15;
  border: 1px solid ${({ theme }) => theme.colors.primary[500]}30;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.primary[400]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.online};
    flex-shrink: 0;
  }
`;

const VolumeRange = styled.input.attrs({ type: 'range' })`
  width: 80px;
  height: 4px;
  background: ${({ theme }) => theme.colors.dark.border};
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary[500]};
    cursor: pointer;
    border: 2px solid ${({ theme }) => theme.colors.dark.surface};
    transition: all ${({ theme }) => theme.transitions.fast};
  }
  
  &::-webkit-slider-thumb:hover {
    background: ${({ theme }) => theme.colors.primary[400]};
    transform: scale(1.1);
  }
  
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary[500]};
    cursor: pointer;
    border: 2px solid ${({ theme }) => theme.colors.dark.surface};
  }
`;

const VolumeContainer = styled.div`
  min-width: 100px;
  text-align: right;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const VolumeLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary[500]};
  font-weight: 600;
  min-width: 35px;
`;

const VoiceSelectButton = styled.button`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary[500]}, ${({ theme }) => theme.colors.primary[600]});
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.smooth};
  box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary[500]}30;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${({ theme }) => theme.colors.primary[500]}40;
  }
  
  &:active {
    transform: translateY(0);
  }
`;



const VoiceSettings = ({ onSettingsChange, highlightVoice }) => {
  const [selectedWakeWord, setSelectedWakeWord] = useState('hey-sarvik');
  const [appVolume, setAppVolume] = useState(100); // App-level volume (SARVIK only)
  const [systemVolume, setSystemVolume] = useState(80); // System-level volume (Windows)
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [audioDevices, setAudioDevices] = useState({ microphones: [], speakers: [] });
  const [selectedMicrophone, setSelectedMicrophone] = useState('default');
  const [selectedSpeaker, setSelectedSpeaker] = useState('default');
  const [actualMicrophone, setActualMicrophone] = useState(null);
  const [actualSpeaker, setActualSpeaker] = useState(null);
  
  // TTS Voice Selector state
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [currentTTSVoice, setCurrentTTSVoice] = useState('lessac');

  // Refs for debouncing
  const deviceDetectionTimeoutRef = useRef(null);
  const deviceListUpdateTimeoutRef = useRef(null);
  const lastDeviceDetectionTimeRef = useRef(0);
  const lastDeviceListUpdateTimeRef = useRef(0);

  // Debounced device detection function
  const debouncedDetectSystemDevices = useCallback(() => {
    // Clear any existing timeout
    if (deviceDetectionTimeoutRef.current) {
      clearTimeout(deviceDetectionTimeoutRef.current);
    }

    // Set new timeout with 500ms delay
    deviceDetectionTimeoutRef.current = setTimeout(() => {
      detectSystemCurrentDevices();
    }, 500);
  }, []);

  // Debounced device list loading function
  const debouncedLoadAudioDevices = useCallback(() => {
    // Clear any existing timeout
    if (deviceListUpdateTimeoutRef.current) {
      clearTimeout(deviceListUpdateTimeoutRef.current);
    }

    // Set new timeout with 1000ms delay
    deviceListUpdateTimeoutRef.current = setTimeout(() => {
      loadAudioDevices();
    }, 1000);
  }, []);

  useEffect(() => {
    // Load saved preferences
    const savedWakeWord = localStorage.getItem('selectedWakeWord');
    const savedAppVolume = localStorage.getItem('appVolume');
    const savedSystemVolume = localStorage.getItem('speakerVolume'); // Keep old key for backward compatibility
    const savedMicrophone = localStorage.getItem('selectedMicrophone');
    const savedSpeaker = localStorage.getItem('selectedSpeaker');
    
    if (savedWakeWord) setSelectedWakeWord(savedWakeWord);
    if (savedAppVolume) setAppVolume(parseInt(savedAppVolume));
    if (savedSystemVolume) setSystemVolume(parseInt(savedSystemVolume));
    if (savedMicrophone) setSelectedMicrophone(savedMicrophone);
    if (savedSpeaker) setSelectedSpeaker(savedSpeaker);
    
    // Load available audio devices initially
    loadAudioDevices();
    
    // Detect what system is currently using
    detectSystemCurrentDevices();
    
    // Listen for automatic device switches from DeviceManager
    const handleDeviceChange = () => {
      console.log('🔄 [VoiceSettings] Device change detected from DeviceManager');
      
      // Use debounced functions to prevent spamming
      debouncedDetectSystemDevices();
      debouncedLoadAudioDevices();
    };
    
    deviceManager.on('deviceChange', handleDeviceChange);
    deviceManager.on('inputDeviceChanged', handleDeviceChange);
    deviceManager.on('outputDeviceChanged', handleDeviceChange);
    
    return () => {
      // Clean up DeviceManager listeners
      deviceManager.removeListener('deviceChange', handleDeviceChange);
      deviceManager.removeListener('inputDeviceChanged', handleDeviceChange);
      deviceManager.removeListener('outputDeviceChanged', handleDeviceChange);
      
      // Clear any pending timeouts
      if (deviceDetectionTimeoutRef.current) {
        clearTimeout(deviceDetectionTimeoutRef.current);
      }
      if (deviceListUpdateTimeoutRef.current) {
        clearTimeout(deviceListUpdateTimeoutRef.current);
      }
    };
  }, [debouncedDetectSystemDevices, debouncedLoadAudioDevices]);
  
  useEffect(() => {
    const handleActualMicChange = (event) => {
      console.log('🎤 [VoiceSettings] Actual microphone changed:', event.detail);
      setActualMicrophone({
        deviceId: event.detail.deviceId,
        label: event.detail.label
      });
    };
    
    const handleDeviceSwitch = (event) => {
      console.log('🔄 [VoiceSettings] Device switch detected:', event.detail);
      // Refresh device detection after switch with debouncing
      debouncedDetectSystemDevices();
      debouncedLoadAudioDevices();
    };
    
    window.addEventListener('actualMicrophoneChanged', handleActualMicChange);
    window.addEventListener('deviceSwitched', handleDeviceSwitch);
    
    return () => {
      window.removeEventListener('actualMicrophoneChanged', handleActualMicChange);
      window.removeEventListener('deviceSwitched', handleDeviceSwitch);
    };
  }, [debouncedDetectSystemDevices, debouncedLoadAudioDevices]);
  
  // Fetch user's TTS voice preference
  useEffect(() => {
    const fetchVoicePreference = async () => {
      try {
        const token = localStorage.getItem('sarvik_token');
        if (!token) return;
        
        const response = await fetch(`http://localhost:8000/api/voice-settings/preference?token=${token}`);
        const data = await response.json();
        
        if (data.success && data.voice) {
          setCurrentTTSVoice(data.voice);
          console.log('✅ Loaded TTS voice preference:', data.voice);
        }
      } catch (error) {
        console.error('Error fetching voice preference:', error);
      }
    };
    
    fetchVoicePreference();
  }, []);

  useEffect(() => {
    if (highlightVoice) {
      setIsHighlighted(true);
      const timer = setTimeout(() => setIsHighlighted(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightVoice]);

  // Initialize app volume manager (controls SARVIK audio only)
  useEffect(() => {
    // Initialize audio volume manager
    audioVolumeManager.initialize();
    
    // Get initial app volume
    const initialAppVol = audioVolumeManager.getVolume();
    if (initialAppVol !== appVolume) {
      setAppVolume(initialAppVol);
    }
    
    // Listen for app volume changes
    const handleAppVolumeChange = (volume) => {
      setAppVolume(volume);
    };
    
    audioVolumeManager.on('volumeChanged', handleAppVolumeChange);
    
    return () => {
      audioVolumeManager.removeListener('volumeChanged', handleAppVolumeChange);
    };
  }, []);

  // Initialize system volume manager (controls Windows volume)
  useEffect(() => {
    let mounted = true;
    
    // Listen for external system volume changes
    const handleSystemVolumeChange = (volume) => {
      if (!mounted) return;
      console.log('🔊 External system volume change detected:', volume);
      setSystemVolume(volume);
      localStorage.setItem('speakerVolume', volume.toString());
    };
    
    volumeManager.on('speakerVolumeChanged', handleSystemVolumeChange);
    
    // Initialize volume manager in background (non-blocking)
    const initializeVolume = async () => {
      try {
        if (!mounted) return;
        
        await volumeManager.initialize();
        
        if (!mounted) return;
        
        // Notify volume manager that settings page is open
        volumeManager.setSettingsOpen(true);
        
        // Get initial system volume
        const initialSystemVol = volumeManager.getSpeakerVolume();
        
        // Update UI with system volume
        if (initialSystemVol !== systemVolume) {
          setSystemVolume(initialSystemVol);
          localStorage.setItem('speakerVolume', initialSystemVol.toString());
        }
        
        console.log('✅ System volume manager initialized');
      } catch (error) {
        console.error('Failed to initialize system volume manager:', error);
      }
    };
    
    // Start initialization in background (doesn't block UI)
    initializeVolume();
    
    return () => {
      mounted = false;
      // Notify volume manager that settings are closing
      volumeManager.setSettingsOpen(false);
      
      // Remove event listeners
      volumeManager.removeListener('speakerVolumeChanged', handleSystemVolumeChange);
    };
  }, []);
  
  // Only run device detection when explicitly needed
  useEffect(() => {
    // Only auto-detect when using default devices
    if (selectedMicrophone === 'default' || selectedSpeaker === 'default') {
      debouncedDetectSystemDevices();
    }
  }, [selectedMicrophone, selectedSpeaker, debouncedDetectSystemDevices]);

  const wakeWordOptions = [
    { id: 'hey-sarvik', text: 'Hey SARVIK' },
    { id: 'sarvik', text: 'SARVIK' },
    { id: 'assistant', text: 'Assistant' },
    { id: 'computer', text: 'Computer' }
  ];

  // ⚠️ OPTIMIZED SYSTEM DEVICE DETECTION: Prevent excessive calls
  const detectSystemCurrentDevices = async () => {
    try {
      // Prevent excessive device detection calls (minimum 1 second between calls)
      const now = Date.now();
      if (now - lastDeviceDetectionTimeRef.current < 1000) {
        console.log('⏭️ [VoiceSettings] Skipping device detection (too frequent)');
        return;
      }
      lastDeviceDetectionTimeRef.current = now;
      
      console.log('🔍 [VoiceSettings] Detecting system current devices...');
      
      // For microphone detection
      if (selectedMicrophone === 'default') {
        const currentDevice = deviceManager.getCurrentInputDevice();
        if (currentDevice) {
          console.log('🎤 [VoiceSettings] Current microphone from DeviceManager:', currentDevice.label);
          setActualMicrophone({
            deviceId: currentDevice.deviceId,
            label: currentDevice.label
          });
        } else {
          const defaultDevice = deviceManager.getSystemDefaultInputDevice();
          if (defaultDevice) {
            console.log('🟡 [VoiceSettings] Using system default microphone:', defaultDevice.label);
            setActualMicrophone({
              deviceId: defaultDevice.deviceId,
              label: defaultDevice.label
            });
          }
        }
      } else {
        const devices = deviceManager.getAvailableInputDevices();
        const selectedDevice = devices.find(device => device.deviceId === selectedMicrophone);
        
        if (selectedDevice) {
          console.log('🟢 [VoiceSettings] Using explicitly selected microphone:', selectedDevice.label);
          setActualMicrophone({
            deviceId: selectedDevice.deviceId,
            label: selectedDevice.label
          });
        }
      }
      
      // Speaker detection
      if (selectedSpeaker === 'default') {
        const currentDevice = deviceManager.getCurrentOutputDevice();
        if (currentDevice) {
          console.log('🔊 [VoiceSettings] Current speaker from DeviceManager:', currentDevice.label);
          setActualSpeaker({
            deviceId: currentDevice.deviceId,
            label: currentDevice.label
          });
        } else {
          const devices = deviceManager.getAvailableOutputDevices();
          const preferredSpeaker = devices.find(device => {
            const label = device.label.toLowerCase();
            return device.deviceId !== 'default' && 
                   device.deviceId !== 'communications' &&
                   (label.includes('bluetooth') || 
                    label.includes('headset') || 
                    label.includes('headphone') ||
                    label.includes('earphone') ||
                    (device.label && device.label !== 'Default'))
          }) || devices.find(device => device.deviceId !== 'default') || devices[0];
          
          if (preferredSpeaker) {
            console.log('🔊 [VoiceSettings] Detected likely active speaker:', preferredSpeaker.label);
            setActualSpeaker({
              deviceId: preferredSpeaker.deviceId,
              label: preferredSpeaker.label
            });
          }
        }
      } else {
        const devices = deviceManager.getAvailableOutputDevices();
        const selectedDevice = devices.find(device => device.deviceId === selectedSpeaker);
        
        if (selectedDevice) {
          console.log('🔊 [VoiceSettings] Using explicitly selected speaker:', selectedDevice.label);
          setActualSpeaker({
            deviceId: selectedDevice.deviceId,
            label: selectedDevice.label
          });
        }
      }
      
    } catch (error) {
      console.error('Failed to detect system devices:', error);
    }
  };

  const loadAudioDevices = async () => {
    try {
      // Prevent excessive device list updates (minimum 2 seconds between calls)
      const now = Date.now();
      if (now - lastDeviceListUpdateTimeRef.current < 2000) {
        console.log('⏭️ [VoiceSettings] Skipping device list update (too frequent)');
        return;
      }
      lastDeviceListUpdateTimeRef.current = now;
      
      console.log('🔄 [VoiceSettings] Loading audio devices...');
      
      const inputDevices = deviceManager.getAvailableInputDevices();
      const outputDevices = deviceManager.getAvailableOutputDevices();
      
      const microphones = inputDevices
        .map(device => ({
          id: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.substr(0, 8)}`,
          isDefault: device.deviceId === 'default'
        }));
      
      const speakers = outputDevices
        .map(device => ({
          id: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.substr(0, 8)}`,
          isDefault: device.deviceId === 'default'
        }));
      
      microphones.sort((a, b) => {
        if (a.isDefault) return -1;
        if (b.isDefault) return 1;
        return a.label.localeCompare(b.label);
      });
      
      speakers.sort((a, b) => {
        if (a.isDefault) return -1;
        if (b.isDefault) return 1;
        return a.label.localeCompare(b.label);
      });
      
      setAudioDevices({ microphones, speakers });
      
    } catch (error) {
      console.error('Failed to load audio devices:', error);
      setAudioDevices({ microphones: [], speakers: [] });
    }
  };

  const handleWakeWordChange = (wakeWordId) => {
    setSelectedWakeWord(wakeWordId);
    localStorage.setItem('selectedWakeWord', wakeWordId);
    onSettingsChange(true);
  };
  
  const handleVolumeChange = async (type, value) => {
    const intValue = parseInt(value);
    
    try {
      if (type === 'app') {
        // App volume (SARVIK only)
        setAppVolume(intValue);
        localStorage.setItem('appVolume', intValue.toString());
        audioVolumeManager.setVolume(intValue);
        onSettingsChange(true);
      } else if (type === 'system') {
        // System volume (Windows master volume)
        setSystemVolume(intValue);
        localStorage.setItem('speakerVolume', intValue.toString());
        await volumeManager.setSpeakerVolume(intValue);
        onSettingsChange(true);
      }
    } catch (error) {
      console.error(`Failed to set ${type} volume:`, error);
      // UI still updates even if system call fails (graceful degradation)
    }
  };

  const handleDeviceChange = async (type, deviceId) => {
    console.log(`🔄 User selected ${type}: ${deviceId}`);
    
    try {
      if (type === 'microphone') {
        setSelectedMicrophone(deviceId);
        localStorage.setItem('selectedMicrophone', deviceId);
        await deviceManager.setInputDevice(deviceId);
        
        if (deviceId === 'default') {
          debouncedDetectSystemDevices(); // Let system choose with debouncing
        } else {
          const selectedDevice = audioDevices.microphones.find(mic => mic.id === deviceId);
          if (selectedDevice) {
            setActualMicrophone({
              deviceId: selectedDevice.id,
              label: selectedDevice.label
            });
          }
        }
        
      } else if (type === 'speaker') {
        setSelectedSpeaker(deviceId);
        localStorage.setItem('selectedSpeaker', deviceId);
        await deviceManager.setOutputDevice(deviceId);
        
        if (deviceId === 'default') {
          debouncedDetectSystemDevices(); // Let system choose with debouncing
        } else {
          const selectedDevice = audioDevices.speakers.find(speaker => speaker.id === deviceId);
          if (selectedDevice) {
            setActualSpeaker({
              deviceId: selectedDevice.id,
              label: selectedDevice.label
            });
          }
        }
      }
      
      onSettingsChange(true);
    } catch (error) {
      console.error(`Failed to change ${type} device:`, error);
    }
  };

  const getDeviceDisplayName = (device, isCurrentDevice = false) => {
    let displayName = device.label;
    
    if (device.isDefault) {
      displayName += ' (System Default)';
    }
    
    return displayName;
  };
  
  const isCurrentlyActiveDevice = (device, actualDevice) => {
    if (!actualDevice) return false;
    return device.id === actualDevice.deviceId || 
           device.label === actualDevice.label ||
           (device.id === 'default' && actualDevice.deviceId === 'default');
  };
  
  const getSelectedDeviceValue = (selectedValue, actualDevice, deviceList) => {
    if (selectedValue !== 'default') {
      return selectedValue;
    }
    
    if (actualDevice && actualDevice.deviceId !== 'default') {
      const matchingDevice = deviceList.find(device => 
        device.id === actualDevice.deviceId || device.label === actualDevice.label
      );
      if (matchingDevice) {
        return matchingDevice.id;
      }
    }
    
    return 'default';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        boxShadow: isHighlighted 
          ? '0 0 20px rgba(43, 147, 255, 0.4)' 
          : '0 0 0px rgba(43, 147, 255, 0)'
      }}
      transition={{ duration: 0.3 }}
      style={{
        border: isHighlighted ? '2px solid #2b93ff' : '2px solid transparent',
        borderRadius: '12px',
        padding: isHighlighted ? '16px' : '0'
      }}
    >
      <SettingsSection>
        <SectionHeader>
          <SectionIcon><Volume2 /></SectionIcon>
          <div>
            <SectionTitle>Device Selection</SectionTitle>
            <SectionDescription>
              Choose your preferred microphone and speaker devices
            </SectionDescription>
          </div>
        </SectionHeader>

        <DeviceSection>
          <SettingRow>
            <DeviceTitleSection>
              <DeviceIcon><Mic /></DeviceIcon>
              <SettingLabel>Microphone Device</SettingLabel>
            </DeviceTitleSection>
          </SettingRow>
          
          <SettingRow>
            <div style={{ width: '100%' }}>
              {actualMicrophone && (
                <CurrentDeviceStatus>
                  <CompactStatusDot />
                  <span>Active: {actualMicrophone.label}</span>
                </CurrentDeviceStatus>
              )}
              <SelectLabel>Choose microphone:</SelectLabel>
              <DeviceSelect
                value={getSelectedDeviceValue(selectedMicrophone, actualMicrophone, audioDevices.microphones)}
                onChange={(e) => handleDeviceChange('microphone', e.target.value)}
                onFocus={debouncedLoadAudioDevices} // Refresh on focus with debouncing
              >
                <option value="default">System Default Microphone</option>
                {audioDevices.microphones.map((mic) => (
                  <option key={mic.id} value={mic.id}>
                    {getDeviceDisplayName(mic)}
                  </option>
                ))}
              </DeviceSelect>
            </div>
          </SettingRow>
        </DeviceSection>
        
        <DeviceSection>
          <SettingRow>
            <DeviceTitleSection>
              <DeviceIcon><Headphones /></DeviceIcon>
              <SettingLabel>Speaker Device</SettingLabel>
            </DeviceTitleSection>
          </SettingRow>
          
          <SettingRow>
            <div style={{ width: '100%' }}>
              {actualSpeaker && (
                <CurrentDeviceStatus>
                  <CompactStatusDot />
                  <span>Active: {actualSpeaker.label}</span>
                </CurrentDeviceStatus>
              )}
              <SelectLabel>Choose speaker:</SelectLabel>
              <DeviceSelect
                value={getSelectedDeviceValue(selectedSpeaker, actualSpeaker, audioDevices.speakers)}
                onChange={(e) => handleDeviceChange('speaker', e.target.value)}
                onFocus={debouncedLoadAudioDevices} // Refresh on focus with debouncing
              >
                <option value="default">System Default Speaker</option>
                {audioDevices.speakers.map((speaker) => (
                  <option key={speaker.id} value={speaker.id}>
                    {getDeviceDisplayName(speaker)}
                  </option>
                ))}
              </DeviceSelect>
            </div>
          </SettingRow>
        </DeviceSection>
      </SettingsSection>

      <SettingsSection>
        <SectionHeader>
          <SectionIcon><Volume2 /></SectionIcon>
          <div>
            <SectionTitle>Volume Control</SectionTitle>
            <SectionDescription>
              Control SARVIK's audio and system volume 
            </SectionDescription>
          </div>
        </SectionHeader>

        <WakeWordSection>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>App Volume</SettingLabel>
              <SettingDescription>
                Adjust SARVIK's voice volume only
              </SettingDescription>
            </SettingInfo>
            <VolumeContainer>
              <VolumeRange
                min="0"
                max="100"
                value={appVolume}
                onChange={(e) => handleVolumeChange('app', e.target.value)}
              />
              <VolumeLabel>
                {appVolume}%
              </VolumeLabel>
            </VolumeContainer>
          </SettingRow>

          <SettingRow>
            <SettingInfo>
              <SettingLabel>System Volume</SettingLabel>
              <SettingDescription>
                Adjust Windows master volume (affects all applications)
              </SettingDescription>
            </SettingInfo>
            <VolumeContainer>
              <VolumeRange
                min="0"
                max="100"
                value={systemVolume}
                onChange={(e) => handleVolumeChange('system', e.target.value)}
              />
              <VolumeLabel>
                {systemVolume}%
              </VolumeLabel>
            </VolumeContainer>
          </SettingRow>
        </WakeWordSection>
      </SettingsSection>

      <SettingsSection>
        <SectionHeader>
          <SectionIcon><Mic /></SectionIcon>
          <div>
            <SectionTitle>Wake Word</SectionTitle>
            <SectionDescription>
              Choose your preferred wake word to activate the assistant
            </SectionDescription>
          </div>
        </SectionHeader>

        <WakeWordSection>
          {wakeWordOptions.map((option) => (
            <SettingRow key={option.id}>
              <SettingInfo>
                <SettingLabel>{option.text}</SettingLabel>
                <SettingDescription>
                  Use "{option.text}" as your wake word
                </SettingDescription>
              </SettingInfo>
              <ToggleSwitch 
                $enabled={selectedWakeWord === option.id}
                onClick={() => handleWakeWordChange(option.id)}
              />
            </SettingRow>
          ))}
      </WakeWordSection>
    </SettingsSection>

    <SettingsSection>
      <SectionHeader>
        <SectionIcon><Radio /></SectionIcon>
        <div>
          <SectionTitle>SARVIK Voice Selection</SectionTitle>
          <SectionDescription>
            Choose your preferred voice for audio responses
          </SectionDescription>
        </div>
      </SectionHeader>

      <WakeWordSection>
        <SettingRow>
          <SettingInfo>
            <SettingLabel>Response Voice</SettingLabel>
            <SettingDescription>
              Select which voice SARVIK uses when responding in voice mode
            </SettingDescription>
          </SettingInfo>
          <VoiceSelectButton onClick={() => setShowVoiceSelector(true)}>
            Select Voice
          </VoiceSelectButton>
        </SettingRow>
      </WakeWordSection>
    </SettingsSection>
    
    <VoiceSelector
      isOpen={showVoiceSelector}
      onClose={() => setShowVoiceSelector(false)}
      currentVoice={currentTTSVoice}
      onVoiceChange={(voice) => {
        setCurrentTTSVoice(voice);
        console.log('TTS voice changed to:', voice);
      }}
    />
  </motion.div>
);
};

export default VoiceSettings;